import React from "react";
import styled from "styled-components";
import tw from "twin.macro";
import Modal from "react-modal";
import { Link } from "react-router-dom";
import GradientButton from "../../components/GradientButton";

const COLORS = {
  teal: "#4f8884",
  tealDark: "#437672",
  gold: "#c6ab57",
  orange: "#A4400A",
};

const LOGIN_URL = "https://www.trataz.com.br/login";
const WHATSAPP_URL = "https://wa.me/5547996912977";

const PlansModalRoot = styled(Modal)`
  position: absolute;
  inset: 50% auto auto 50%;
  transform: translate(-50%, -50%);
  width: min(940px, calc(100vw - 32px));
  max-height: calc(100vh - 32px);
  overflow: auto;
  overflow-x: hidden;
  background: #fff;
  border-radius: 8px;
  outline: none;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.24);
`;

const ModalInner = styled.div`
  ${tw`relative`}
  padding: 2.75rem 2rem 3rem;
  overflow: hidden;
  background: #fff;

  &::after {
    content: "";
    position: absolute;
    top: -120px;
    right: -280px;
    width: 720px;
    height: 890px;
    background-image: url("/novas-imagens/popup-planos/expiral.png");
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
    opacity: 0.13;
    pointer-events: none;
  }

  @media (min-width: 1024px) {
    padding: 3.9rem 3.9rem 3.7rem;
  }

  @media (max-width: 767px) {
    padding: 2.45rem 1.35rem 2.6rem;
    overflow-x: hidden;

    &::after {
      top: -80px;
      right: -310px;
      width: 560px;
      height: 690px;
    }
  }
`;

const ModalClose = styled.button`
  ${tw`absolute`}
  top: 1rem;
  right: 1rem;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 9999px;
  background: rgba(0, 0, 0, 0.06);
  color: #444;
  font-size: 20px;
  cursor: pointer;
  z-index: 3;
`;

const ModalTop = styled.div`
  ${tw`relative grid gap-8`}
  z-index: 1;
  min-width: 0;

  @media (min-width: 1024px) {
    grid-template-columns: 290px 220px;
    align-items: start;
    column-gap: 4.1rem;
  }
`;

const ModalBrand = styled.div``;

const ModalLogo = styled.img`
  width: 214px;
  height: auto;
`;

const ModalIntro = styled.p`
  margin: 1.7rem 0 0;
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  line-height: 1.16;
  color: #5a5a5a;
  text-transform: uppercase;

  strong {
    font-weight: 700;
  }
`;

const ModalTopCards = styled.div`
  ${tw`relative`}
  min-width: 0;

  @media (min-width: 1024px) {
    width: 220px;
  }
`;

const PlanCard = styled.div`
  ${tw`relative`}
  background: #fff;
  border: 2px solid #9c8539;
  border-radius: ${({ large }) => (large ? "28px" : "24px")};
  padding: ${({ large }) =>
    large ? "1.45rem 1.55rem 1.7rem" : "1.05rem 1.35rem 1.45rem"};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.18);
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 767px) {
    border-radius: ${({ large }) => (large ? "24px" : "22px")};
    padding: ${({ large }) =>
      large ? "1.2rem 1.25rem 1.55rem" : "0.95rem 1.2rem 1.35rem"};
  }
`;

const SmallTrialCard = styled(PlanCard)`
  align-self: start;
`;

const PlanTitle = styled.h4`
  margin: 0;
  font-family: "Montserrat", sans-serif;
  font-size: ${({ small }) => (small ? "1.05rem" : "1.65rem")};
  font-weight: 700;
  color: ${COLORS.tealDark};
  text-transform: uppercase;
`;

const PlanSubtitle = styled.p`
  margin: 0.75rem 0 0;
  font-family: "Montserrat", sans-serif;
  font-size: 0.64rem;
  line-height: 1.15;
  color: ${COLORS.tealDark};
  font-weight: 700;
  text-transform: uppercase;
`;

const PlanButton = styled(GradientButton).attrs({
  $variant: "teal",
  $borderWidth: "2px",
})`
  margin-top: ${({ compact }) => (compact ? "-1.45rem" : "-0.9rem")};
  margin-left: 1.25rem;
  min-width: ${({ compact }) => (compact ? "142px" : "150px")};
  height: ${({ compact }) => (compact ? "37px" : "40px")};
  padding: 0 24px;
  font-family: "Montserrat", sans-serif;
  font-size: ${({ compact }) => (compact ? "10px" : "11px")};
  font-weight: 500;
  position: relative;
  z-index: 2;
  transform: ${({ compact }) => (compact ? "translateY(-10px)" : "none")};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.22);

  @media (max-width: 767px) {
    min-width: ${({ compact }) => (compact ? "132px" : "142px")};
    height: ${({ compact }) => (compact ? "35px" : "38px")};
    padding: 0 20px;
  }
`;

const CenteredPlanButton = styled(PlanButton)`
  margin-left: auto;
  margin-right: auto;
  display: flex;
`;

const RightShiftedPlanButton = styled(PlanButton)`
  margin-left: auto;
  margin-right: 0;
  display: flex;
`;

const CenteredButtonRow = styled.div`
  display: flex;
  justify-content: center;
`;

const RightButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-right: 3.5rem;
`;

const ModalMiddle = styled.div`
  ${tw`relative grid gap-8 mt-10`}
  z-index: 1;
  min-width: 0;

  @media (min-width: 1024px) {
    grid-template-columns: 300px 280px;
    align-items: start;
    column-gap: 4.9rem;
  }
`;

const RightPlansColumn = styled.div`
  min-width: 0;

  @media (min-width: 1024px) {
    margin-top: -2.6rem;
  }
`;

const FeaturedPlan = styled(PlanCard)`
  min-height: 405px;
`;

const PopularTag = styled.div`
  position: absolute;
  top: 1.6rem;
  right: -26px;
  padding: 0.7rem 1rem;
  background: linear-gradient(90deg, #b59a44 0%, #ddd2a0 45%, #c1a84f 100%);
  color: #4d4b4b;
  font-family: "Montserrat", sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;

  @media (max-width: 767px) {
    right: 0;
    padding: 0.55rem 0.75rem;
    max-width: 150px;
    font-size: 0.72rem;
    text-align: center;
  }
`;

const PlanDesc = styled.p`
  margin: 0.55rem 0 0;
  font-family: "Montserrat", sans-serif;
  font-size: 0.82rem;
  line-height: 1.15;
  color: #437672;
  font-weight: 700;
  text-transform: uppercase;
`;

const PriceBlock = styled.div`
  margin-top: 1.1rem;
  font-family: "Montserrat", sans-serif;
  color: ${COLORS.tealDark};
  font-weight: 700;
  line-height: 1.12;
  font-size: 0.9rem;
`;

const PriceLine = styled.div`
  margin-top: 0.85rem;
`;

const FeatureBulletList = styled.ul`
  ${tw`grid gap-2`}
  margin: 1.45rem 0 0;
  padding-left: 1.15rem;
  color: #2f2f2f;
  font-family: "Montserrat", sans-serif;
  font-size: 0.72rem;
  line-height: 1.18;
`;

const SidePlan = styled(PlanCard)`
  min-height: 225px;
`;

const SideNotes = styled.div`
  margin-top: 1.8rem;
  font-family: "Montserrat", sans-serif;
  font-size: 1.1rem;
  line-height: 1.15;
  color: #5a5a5a;
  text-transform: uppercase;
`;

const NoteTitle = styled.div`
  color: ${COLORS.orange};
  font-weight: 700;
  text-transform: uppercase;
`;

const NoteBody = styled.p`
  margin: 0.18rem 0 0;

  strong {
    color: ${COLORS.orange};
    font-weight: 700;
  }
`;

const TealNoteTitle = styled.div`
  margin-top: 1.2rem;
  color: #437672;
  font-weight: 700;
  text-transform: uppercase;
`;

const SideNoteButton = styled(GradientButton).attrs({
  $variant: "teal",
  $borderWidth: "2px",
})`
  margin-top: 1rem;
  min-width: 128px;
  height: 34px;
  padding: 0 18px;
  font-family: "Montserrat", sans-serif;
  font-size: 0.62rem;
  font-weight: 500;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.22);
`;

const BottomPlanWrap = styled.div`
  ${tw`relative mt-8`}
  z-index: 1;
`;

const WidePlan = styled(PlanCard)`
  padding-right: 1.7rem;

  @media (min-width: 1024px) {
    display: grid;
    grid-template-columns: 235px 1fr;
    column-gap: 2.3rem;
    align-items: start;
  }
`;

const WideFeatureList = styled.ul`
  ${tw`grid gap-2`}
  margin: 1rem 0 0;
  padding-left: 1.15rem;
  color: #2f2f2f;
  font-family: "Montserrat", sans-serif;
  font-size: 0.72rem;
  line-height: 1.18;

  @media (min-width: 1024px) {
    margin-top: 2.2rem;
  }
`;

const ModalFooterNote = styled.div`
  ${tw`flex flex-col items-start gap-2 mt-12 text-left`}
  z-index: 1;
  position: relative;

  @media (min-width: 768px) {
    align-items: flex-start;
    justify-content: flex-start;
    text-align: left;
  }
`;

const AtendimentoPill = styled(GradientButton).attrs({
  $variant: "teal",
  $borderWidth: "2px",
})`
  min-width: 128px;
  height: 31px;
  padding: 0 16px;
  font-family: "Montserrat", sans-serif;
  font-size: 0.58rem;
  line-height: 1.1;
  font-weight: 500;
`;

const FooterMessage = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 0.74rem;
  line-height: 1.15;
  color: #437672;
  font-weight: 700;
  text-transform: uppercase;
  width: 360px;
  max-width: 100%;
`;

const FooterClick = styled.div`
  color: ${COLORS.orange};
  font-family: "Montserrat", sans-serif;
  font-size: 0.76rem;
  font-weight: 700;
  text-transform: uppercase;
`;

export default function PlansModal({ isOpen, onRequestClose }) {
  return (
    <PlansModalRoot
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      shouldCloseOnOverlayClick
      overlayClassName="plans-modal-overlay"
    >
      <ModalInner>
        <ModalClose onClick={onRequestClose} aria-label="Fechar modal">
          ×
        </ModalClose>

        <ModalTop>
          <ModalBrand>
            <ModalLogo
              src="/novas-imagens/popup-planos/tratazLogo.png"
              alt="Trataz"
            />
            <ModalIntro>
              Escolha o <strong>plano ideal</strong>
              <br />
              para sua prática profissional.
              <br />
              Comece <strong>gratuitamente</strong> e escale
              <br />
              conforme sua clínica cresce.
            </ModalIntro>
          </ModalBrand>

          <ModalTopCards>
            <SmallTrialCard>
              <PlanTitle>Trial</PlanTitle>
              <PlanSubtitle>
                acesso a todos os recursos
                <br />
                da plataforma por 14 dias
              </PlanSubtitle>
            </SmallTrialCard>
            <PlanButton
              compact
              as={Link}
              to={LOGIN_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              COMEÇAR
            </PlanButton>
          </ModalTopCards>
        </ModalTop>

        <ModalMiddle>
          <div>
            <FeaturedPlan large>
              <PopularTag>Mais popular</PopularTag>
              <PlanTitle>Starter</PlanTitle>
              <PlanDesc>
                ideal para
                <br />
                profissionais iniciantes
              </PlanDesc>

              <PriceBlock>
                <PriceLine>
                  Mensal: R$ 98,70 <small>(mês)</small>
                </PriceLine>
                <PriceLine>
                  Semestral: R$ 533,00
                  <br />
                  R$ 8,83 <small>(mês)</small>
                </PriceLine>
                <PriceLine>
                  Anual: R$ 947,52
                  <br />
                  R$ 78,96 <small>(mês)</small>
                </PriceLine>
              </PriceBlock>

              <FeatureBulletList>
                <li>ATÉ 30 PACIENTES</li>
                <li>MATERIAIS DIDÁTICOS COMPLETOS</li>
                <li>LEMBRETES POR WHATSAPP E E-MAIL</li>
                <li>VÍDEOS PERSONALIZADOS BÁSICOS</li>
                <li>RELATÓRIOS DE PROGRESSO</li>
                <li>SUPORTE PRIORITÁRIO</li>
              </FeatureBulletList>
            </FeaturedPlan>
            <CenteredButtonRow>
              <CenteredPlanButton
                as={Link}
                to={LOGIN_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                COMEÇAR
              </CenteredPlanButton>
            </CenteredButtonRow>
          </div>

          <RightPlansColumn>
            <SidePlan>
              <PlanTitle>Free+ R$0</PlanTitle>
              <PlanSubtitle>acesso limitado, porém contínuo</PlanSubtitle>
              <FeatureBulletList>
                <li>ATÉ 5 PACIENTES</li>
                <li>MATERIAIS DIDÁTICOS BÁSICOS</li>
                <li>LEMBRETES POR E-MAIL</li>
                <li>SUPORTE POR E-MAIL</li>
                <li>ACESSO POR 14 DIAS</li>
              </FeatureBulletList>
            </SidePlan>
            <PlanButton
              compact
              as={Link}
              to={LOGIN_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              COMEÇAR
            </PlanButton>

            <ModalFooterNote>
              <FooterMessage>
                Para todos os planos, seu paciente poderá imprimir o relatório
                de prontuário.
              </FooterMessage>
              <FooterClick>CLIQUE AQUI</FooterClick>
              <AtendimentoPill
                as={Link}
                to={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                ATENDIMENTO TRATAZ
              </AtendimentoPill>
            </ModalFooterNote>
          </RightPlansColumn>
        </ModalMiddle>

        <BottomPlanWrap>
          <div>
            <WidePlan large>
              <div>
                <PlanTitle>Clinic</PlanTitle>
                <PlanDesc>
                  para clínicas estabelecidas e profissionais experientes
                </PlanDesc>

                <PriceBlock>
                  <PriceLine>
                    Mensal: R$ 297,00 <small>(mês)</small>
                    <br />
                    <small>(até 3 usuários)</small>
                  </PriceLine>
                  <PriceLine>
                    Semestral: R$ 1.603,50
                    <br />
                    R$ 267,25 <small>(mês)</small>
                  </PriceLine>
                  <PriceLine>
                    Anual: R$ 2.851,00
                    <br />
                    R$ 237,58 <small>(mês)</small>
                  </PriceLine>
                  <PriceLine>Usuário extra → R$ 59/mês</PriceLine>
                </PriceBlock>
              </div>

              <WideFeatureList>
                <li>PACIENTES ILIMITADOS</li>
                <li>TODOS OS RECURSOS DO STARTER</li>
                <li>VÍDEOS PERSONALIZADOS AVANÇADOS</li>
                <li>INTEGRAÇÃO COM AGENDA</li>
                <li>RELATÓRIOS AVANÇADOS E ANALYTICS</li>
                <li>SUPORTE 24/7 VIA WHATSAPP</li>
                <li>TREINAMENTO PERSONALIZADO</li>
                <li>API PARA INTEGRAÇÕES</li>
              </WideFeatureList>
            </WidePlan>
            <RightButtonRow>
              <RightShiftedPlanButton
                as={Link}
                to={LOGIN_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                COMEÇAR
              </RightShiftedPlanButton>
            </RightButtonRow>
          </div>
        </BottomPlanWrap>

        <SideNotes>
          <NoteTitle>Importante:</NoteTitle>
          <NoteBody>
            Os valores podem ser ajustados após <strong>validação.</strong>
          </NoteBody>

          <TealNoteTitle>Empresa/Instituição:</TealNoteTitle>
          <NoteBody>Fale conosco</NoteBody>
          <SideNoteButton
            as={Link}
            to={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            CLIQUE AQUI
          </SideNoteButton>
        </SideNotes>
      </ModalInner>
    </PlansModalRoot>
  );
}
